import { type DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { CorePlugin, PublicPluginAPI, SelectionContext } from '@atlaskit/editor-common/types';
import { type JSONNode } from '@atlaskit/editor-json-transformer';
import type { UserIntentPlugin } from '@atlaskit/editor-plugin-user-intent';
import type { RemixFeedbackMetadata } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal/SmartCreationContext';

import type { RemixHiddenOptionKind } from '../../types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports -- RemixOptionKind is the canonical type owned by the sibling remix-overlay module; duplicating it here would drift.
import type { RemixOptionKind } from '../remix/remix-overlay';

import type { ContentClassification } from './utils/content-classification';
export type { ContentClassification } from './utils/content-classification';

export type EditorApiWithCorePlugin = PublicPluginAPI<[CorePlugin, UserIntentPlugin]>;

export type DynamicUiSource = {
	contentType: string;
	endIndex: number;
	endLocalId: string;
	fragmentAdf: JSONNode[] | null;
	fullAdf: DocNode;
	startIndex: number;
	startLocalId: string;
};

export type RemixModalMetadataLike = {
	[key: string]: unknown;
	aspectRatio?: string | null;
	imageStyle?: string | null;
	dynamicUiType?: RemixOptionKind;
	dynamicUiSubtype?: string;
	interactionSource?: InteractionSource;
	userPrompt?: string;
	onCancel?: () => void;
	aiFeatureName?: string;
};

export type AddRemixHistoryEntryPayload = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	finalMessage: StoreAssistant;
	/** Classification captured from ACTION_CLASSIFICATION plus any content-specific stream data. */
	contentClassification?: ContentClassification;
	/** for image, there should be image fieldId */
	fieldId?: string;
	/** for MAUI extensions, there should be MAUI url */
	url?: string;
};

export type RemixHistoryEntry = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage: StoreAssistant;
	latestFinalMessageId?: string;
	stack: Array<{
		dynamicUiSubtype?: string;
		dynamicUiType: string;
		fieldId?: string;
		finalMessage: StoreAssistant;
		finalMessageId?: string;
		url?: string;
	}>;
};

export type RemixHistoryEntryForUpdate = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage?: StoreAssistant;
	latestFinalMessageId?: string;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	finalMessageId?: string;
	url?: string;
};

export type UpdateRemixImageHistoryFieldIdPayload = {
	conversationId: string;
	fieldId: string;
};

export type AddRemixHistoryStackEntryPayload = {
	conversationId: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	previousFieldId?: string;
	previousUrl?: string;
	url?: string;
};

/**
 * Dynamic-ui (MAUI) variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates a MAUI app on the page.
 */
export type AddBackendMauiRemixHistoryStackEntryPayload = {
	/** The MAUI app id being replaced. */
	previousMauiId: string;
	/** New MAUI app URL synthesized from the BE-side new app id. */
	url: string;
	/** Inherits from the matched stack entry when omitted. */
	dynamicUiType?: string;
	/** Backend-confirmed subtype from a type-changing edit; inherited from the matched entry when omitted. */
	dynamicUiSubtype?: string;
};

/**
 * Image variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates an image on the page.
 */
export type AddBackendImageRemixHistoryStackEntryPayload = {
	/** The media file id being replaced (BE-side previousMediaFileId). */
	previousFieldId: string;
	/** Live upload-side file id from onUploadAndInsertMedia's result. */
	fieldId: string;
};

/**
 * Payload for appending a stack entry from a backend-initiated remix update
 * (bridge stream). The reducer locates the existing entry by matching the
 * previous identifier against stack entries' MAUI app id (extracted from url)
 * or fieldId, then inherits `finalMessage`, `finalMessageId`, and (when omitted)
 * `dynamicUiType`/`dynamicUiSubtype` from the matched entry.
 *
 * Discriminated union of the dynamic-ui (MAUI) and image variants;
 * narrow with {@link isAddBackendMauiRemixHistoryStackEntryPayload}.
 */
export type AddBackendRemixHistoryStackEntryPayload =
	| AddBackendMauiRemixHistoryStackEntryPayload
	| AddBackendImageRemixHistoryStackEntryPayload;

/**
 * Type guard discriminating the dynamic-ui (MAUI) variant of
 * {@link AddBackendRemixHistoryStackEntryPayload} from the image variant.
 * The two branches have disjoint required fields (`previousMauiId` vs
 * `previousFieldId`), so an `in`-check on either is a safe discriminator.
 */
export function isAddBackendMauiRemixHistoryStackEntryPayload(
	payload: AddBackendRemixHistoryStackEntryPayload,
): payload is AddBackendMauiRemixHistoryStackEntryPayload {
	return 'previousMauiId' in payload;
}

/**
 * Props specific to the remix type.
 */
export type RemixTypeProps =
	| {
			remixType?: 'block';
	  }
	| {
			remixType: 'object';
			adfDocument: DocNode | Promise<DocNode>;
	  };

export type RemixContentComponentProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixTypeProps & {
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;
	/** See `EditorRemixContext.onVariantUpdateComplete`. */
	onVariantUpdateComplete?: (id: string) => void;
	/** See `EditorRemixContext.onSubtypeMismatch`. */
	onSubtypeMismatch?: () => void;
	clearThinkingDecoration?: () => void;
	replaceWhiteboardEmbed?: (options: {
		contentId: string;
		finalizedUrl: string;
		localId?: string;
	}) => void;
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;
	editorApi?: TEditorApi;
	handleModalClose: () => void;
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	isComplete?: boolean;
	isModalOpen: boolean;
	isThinking: boolean;
	isUpdate?: boolean;
	isViewMode?: boolean;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	dynamicUiSubtypeForUpdate?: string;
	showLocationPicker?: boolean;
	setThinkingComplete?: (isComplete: boolean) => void;
	finishRemixThinking?: () => void;
	onRemixThinkingCompleteAnimationFinished?: () => void;
	onStartThinking: (options?: { contentType?: string }) => void;
	onDismissThinkingUi?: (options?: { reopenModal?: boolean }) => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	onRegisterAbortRequest?: (abortFn: () => void) => () => void;
	setShowLocationPicker?: (showLocationPicker: boolean) => void;
	/**
	 * Product-supplied recovery handler invoked when automatic whiteboard
	 * finalization fails. Threaded into {@link RemixProductIntegration} by the
	 * product wrapper. See {@link RemixProductIntegration.onFinalizeFailed}.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	remixModalMetadata?: RemixModalMetadataLike;
	remixSource?: string;
	selectedContext: SelectionContext;
};

export type RemixContentComponentFullProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixContentComponentProps<TEditorApi> & {
	entryPoint?: string;
	productConfig: RemixProductConfig;
	productIntegration: RemixProductIntegration;
	smartCreationConfig?: Pick<ConversationAssistantConfig, 'siteId' | 'userId' | 'product'>;
	smartCreationContext?: SmartCreationContext;
};

export type OpenRemixPreviewOptions = {
	isFloating: boolean;
	remixConversationId?: string;
};

export type RemixInsertSuccessPayload = {
	currentConversationId?: string;
	remixMetadata?: RemixFeedbackMetadata;
	requestId?: string;
};

export type RemixFinalizeFailedPayload = {
	/** The remixed whiteboard's content id (parsed from the embed URL). */
	contentId: string;
	/** The `~ROVO…` embed URL currently present in the document. */
	originalUrl: string;
};

export type RemixFinalizeSuccessPayload = {
	/** The remixed whiteboard's content id. */
	contentId: string;
	/** The parent used when the whiteboard was finalized. Root-level content has no parent. */
	parentId: string | undefined;
};

export type RemixProductIntegration = {
	closeRemixPreview: () => void;
	contentType: string;
	isRemixPreviewVisible: boolean;
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;
	/**
	 * Fired after automatic finalization moves a remixed whiteboard into the
	 * product space.
	 */
	onFinalizeSuccess?: (payload: RemixFinalizeSuccessPayload) => void;
	/**
	 * Fired when automatic finalization of a remixed whiteboard fails. Product
	 * integrations may take over the recovery UX (e.g. open a space picker) and
	 * return `true` to signal the failure was handled.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	openRemixPreview: (options: OpenRemixPreviewOptions) => void;
	toggleConversationAssistant?: (location: string) => void;
};

export type RemixProductConfig = {
	analyticsProductName?: string;
	isActionReady?: (chatMessages: StoreChatItem[]) => boolean;
	isNodeUnsupportedForRemix?: (node: ADFEntity) => boolean;
	productKey: string;
	shouldUseEphemeralPreview?: (chatMessages: StoreChatItem[]) => boolean;
};
